import type { _AxiosRequestConfig } from 'axios'
import { initRegion } from '@/consts'
import { die } from '@/store/root.ts'
import axios, { AxiosError } from 'axios'

const service = axios.create({
  /** 是否携带凭证 */
  withCredentials: false,
  /** 超时时间，默认30s */
  timeout: 30 * 1000,
})

/** 请求池 */
const pending = new Map<string, AbortController['abort']>()

/**
 * 删除请求池中的请求
 * @param config
 */
const removePending = (config?: _AxiosRequestConfig) => {
  if (!config) return

  const um = `${config.url}&${config.method}`
  if (pending.has(um)) {
    pending.get(um)!()
    pending.delete(um)
  }
}

service.interceptors.request.use(
  config => {
    removePending(config)
    const controller = new AbortController()
    if (config.allowParallel !== true) {
      pending.set(`${config.url}&${config.method}`, controller.abort.bind(controller))
    }
    const region = getLocal<Global.OptionItem[]>('region', { crypto: true }) || initRegion

    return {
      baseURL: config.url?.includes('mock') ? '' : import.meta.env.VITE_BASE_URL,
      ...config,
      headers: {
        token: getLocal('jwtToken', { crypto: true }),
        region: JSON.stringify(region.map(item => item.value)),
        ...config.headers,
      },
    }
  },
  error => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  ({ request, status, data: res, config }) => {
    removePending(config)
    // 原生 http 状态码
    if (status === 200) {
      if (['blob', 'arraybuffer'].includes(request.responseType)) {
        return res
      }
      // 后端自定义状态码
      const { code, message: msg } = res
      /* 白名单判断 ****************************/
      const silentCodes = config.silentCodes ?? []
      const needToast = !silentCodes.includes(code)
      /**************************************************/
      switch (code) {
        case 200:
          return res
        case 401:
          removeLocal('jwtToken')
          message.warning(msg)
          setTimeout(() => {
            window.location.reload()
          }, 1500)
          return res
        case 403:
          needToast && message.warning(msg)
          return res
        // 服务器异常
        case 500:
          needToast && message.warning(msg)
          return res
        default:
          needToast && message.warning(msg)
          return res
      }
    }
    return Promise.reject(new Error(res.message))
  },
  error => {
    const { code, status } = error
    if (code === AxiosError.ERR_CANCELED) {
      return Promise.reject(new Error('请求被取消'))
    }
    if (code === AxiosError.ERR_BAD_REQUEST && status === 401) {
      message.warning('登录授权已过期，请重新登录')
      die()
      return Promise.reject(new Error(error.message))
    }
    if (error.message.includes('403')) {
      message.warning('登录授权已过期，请重新登录')
      removeLocal('jwtToken')
      return Promise.reject(new Error(error.message))
    }
    if (code === AxiosError.ETIMEDOUT) {
      message.error('网络请求超时，请稍候再试')
      return Promise.reject(new Error(error.message))
    }
    if (code === AxiosError.ERR_NETWORK) {
      message.error('网络异常，请稍候再试')
      return Promise.reject(new Error(error.message))
    }
    error.message && message.error(error.message)
    return Promise.reject(new Error(error.message))
  }
)

export default service
